import { useCallback } from 'react'
import { Box, Stack } from '@mui/material'
import { ProjectGridField } from '@/types/fields'
import { APPROX_CHAR_WIDTH } from '@/utils/field'
import { isTextInputFieldType } from '@/utils/typescript'
import { useDocumentComparisonDataContext } from '@/components/image-zoom-pan/providers/DocumentComparisonDataProvider'
import { useContainerSize } from '@/components/size-provider/SizeProvider'
import Field from './Field'
import FieldTable, { ROW_HEIGHT } from './FieldTable'
import useGetValueData from './useGetValueData'

type FieldsComparisonProps = {
  groupedFields: ProjectGridField[][]
  side: 'left' | 'right'
  documentId: string
}

const TABLE_HEIGHT_WITHOUT_ROWS = 100.25
const DEFAULT_FIELD_WIDTH = 400

function useGetTextInputFieldSx({ documentId }: { documentId: string }) {
  const getValueData = useGetValueData()

  const getTextInputFieldSx = useCallback(
    (
      fieldId: string,
      containerWidth: number,
      fieldHeights?: [string, number][],
    ) => {
      if (!containerWidth) {
        return { width: DEFAULT_FIELD_WIDTH }
      }
      const { dataLineageValues } = getValueData({ fieldId })
      const valueLength = dataLineageValues.final_value
        ? dataLineageValues.final_value.length
        : 0

      const valueCharsApproxWidth = valueLength * APPROX_CHAR_WIDTH
      const width = Math.min(
        Math.max(DEFAULT_FIELD_WIDTH, valueCharsApproxWidth),
        containerWidth - 16,
      )

      const currentFieldHeight =
        fieldHeights?.find(([docId]) => documentId === docId)?.[1] || 0

      const otherFieldHeight =
        fieldHeights?.find(([docId]) => documentId !== docId)?.[1] || 0

      const padding = Math.abs(otherFieldHeight - currentFieldHeight)

      return {
        width,
        pb: otherFieldHeight > currentFieldHeight ? `${padding}px` : undefined,
      }
    },
    [documentId, getValueData],
  )

  return getTextInputFieldSx
}

export default function FieldsComparison({
  groupedFields,
  side,
  documentId,
}: FieldsComparisonProps) {
  const { rowsCountByGridId, baseFieldHeights } =
    useDocumentComparisonDataContext()
  const { width } = useContainerSize()
  const getTextInputFieldSx = useGetTextInputFieldSx({ documentId })

  return (
    <Stack
      spacing={1}
      alignItems={side === 'left' ? 'flex-end' : undefined}
      sx={{
        width: '100%',
        p: 1,
      }}
    >
      {groupedFields.map((group, index) => {
        if (group.length === 0) return null
        const isTableField = group[0].fields
        let paddingBottom = undefined

        if (
          isTableField &&
          rowsCountByGridId &&
          group[0].sub_project_grid_id &&
          rowsCountByGridId[group[0].sub_project_grid_id]
        ) {
          const rowsCount = rowsCountByGridId[group[0].sub_project_grid_id]
          const mainRowsCount = rowsCount['main']
          const secondaryRowsCount = rowsCount['secondary']

          const mainHeight =
            mainRowsCount > 0
              ? mainRowsCount * ROW_HEIGHT
              : TABLE_HEIGHT_WITHOUT_ROWS
          const secondaryHeight =
            secondaryRowsCount > 0
              ? secondaryRowsCount * ROW_HEIGHT
              : TABLE_HEIGHT_WITHOUT_ROWS

          // Add padding to the shorter table
          if (side === 'right' && mainHeight < secondaryHeight) {
            paddingBottom = secondaryHeight - mainHeight
          } else if (side === 'left' && secondaryHeight < mainHeight) {
            paddingBottom = mainHeight - secondaryHeight
          }
        }

        return isTableField ? (
          <Box
            key={index}
            sx={{ mx: -1, width: '100%', pb: `${paddingBottom}px` }}
          >
            <FieldTable tableField={group[0]} />
          </Box>
        ) : (
          group.map((field) => {
            return (
              <Box
                key={field.id}
                sx={{
                  ...(isTextInputFieldType(field.project_grid_field_type.code)
                    ? getTextInputFieldSx(
                        field.id,
                        width,
                        baseFieldHeights[field.id],
                      )
                    : {
                        width: width
                          ? Math.min(DEFAULT_FIELD_WIDTH, width - 16)
                          : DEFAULT_FIELD_WIDTH,
                      }),
                }}
              >
                <Field field={field} />
              </Box>
            )
          })
        )
      })}
    </Stack>
  )
}
